import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInitialFormData, useEmployeeFormHandlers } from './CreateEmployeeForm';
import { JoinRequestStatus, type JoinRequestInfo } from '../../types';
import { useAcceptJoinRequest, useDeclineJoinRequest, useJoinRequest, useUserRoles } from '../../hooks';
import { JoinRequestFormContent, type ContactMode } from './JoinRequestFormContent';

interface JoinRequestProps {
    companyId: string;
    onSuccess: () => void;
    joinRequest: JoinRequestInfo;
}

export function JoinRequest({ companyId, onSuccess, joinRequest }: JoinRequestProps) {
    const { t } = useTranslation();
    const {
        formData, setFormData,
        errors, setErrors,
        handleChange, handleToggleMainContact,
        isFormValid,
    } = useEmployeeFormHandlers(joinRequest.userEmail, joinRequest.userName);

    const [contactMode, setContactMode] = useState<ContactMode>('create');
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

    const { data: roles, isLoading: rolesLoading } = useUserRoles(companyId);
    const acceptJoinRequest = useAcceptJoinRequest(companyId);
    const declineJoinRequest = useDeclineJoinRequest(companyId);
    const { data: joinRequestStatus, isError: isStatusError, isLoading: isStatusLoading } = useJoinRequest(companyId, joinRequest?.id);

    const isPending = !isStatusError && (!joinRequestStatus || joinRequestStatus.status === JoinRequestStatus.PENDING);

    const handleContactModeChange = useCallback((mode: ContactMode) => {
        setContactMode(mode);
        setSelectedContactId(null);
    }, []);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            if (contactMode === 'create' && !isFormValid) return;
            if (contactMode === 'select' && !selectedContactId) return;

            setErrors({});

            try {
                if (contactMode === 'create') {
                    await acceptJoinRequest.mutateAsync({
                        requestId: joinRequest.id,
                        contact: {
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            phoneNumber: formData.phoneNumber || undefined,
                            email: formData.email,
                            roleId: formData.roleId,
                            isMainContact: formData.isMainContact,
                        },
                    });
                } else {
                    await acceptJoinRequest.mutateAsync({
                        requestId: joinRequest.id,
                        contactId: selectedContactId!,
                    });
                }
                setFormData(createInitialFormData(joinRequest.userEmail, joinRequest.userName));
                setSelectedContactId(null);
                onSuccess?.();
            } catch {
                setErrors((prev) => ({ ...prev, general: t('employees.createForm.unknownError') }));
            }
        },
        [contactMode, isFormValid, selectedContactId, formData, acceptJoinRequest, joinRequest, onSuccess, t]
    );

    const handleDecline = useCallback(
        async () => {
            try {
                await declineJoinRequest.mutateAsync(joinRequest.id);
                onSuccess?.();
            } catch {
                setErrors((prev) => ({ ...prev, general: t('employees.createForm.unknownError') }));
            }
        },
        [joinRequest, declineJoinRequest, onSuccess, t]
    );

    if (isStatusLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 space-y-4 rounded-lg border bg-surface border-border">
                <div className="animate-spin inline-block size-8 border-2 border-current border-t-transparent rounded-full" />
                <h3 className="text-lg font-semibold text-onsurface">{t('employees.createForm.joinRequest.loading')}</h3>
            </div>
        );
    }

    if (isStatusError) {
        return (
            <div className="flex flex-col items-center justify-center py-12 space-y-4 rounded-lg border bg-surface border-border">
                <svg className="size-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-onsurface">{t('employees.createForm.joinRequest.error.title')}</h3>
                <p className="text-sm text-muted-foreground text-center px-4">{t('employees.createForm.joinRequest.error.message')}</p>
            </div>
        );
    }

    if (!isPending && joinRequestStatus && !isStatusError) {
        return <ResolvedJoinRequestStatus joinRequestStatus={joinRequestStatus.status} />
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6 px-6">
            <JoinRequestFormContent
                formData={formData}
                errors={errors}
                roles={roles}
                rolesLoading={rolesLoading}
                handleChange={handleChange}
                handleToggleMainContact={handleToggleMainContact}
                handleDecline={handleDecline}
                joinRequest={joinRequest}
                isFormValid={isFormValid}
                isAccepting={acceptJoinRequest.isPending}
                isDeclining={declineJoinRequest.isPending}
                companyId={companyId}
                contactMode={contactMode}
                onContactModeChange={handleContactModeChange}
                selectedContactId={selectedContactId}
                onSelectedContactChange={setSelectedContactId}
            />
        </form>
    );
}


function ResolvedJoinRequestStatus({ joinRequestStatus }: { joinRequestStatus: JoinRequestStatus }) {
    const { t } = useTranslation();

    const statusConfig: Record<Exclude<JoinRequestStatus, JoinRequestStatus.PENDING>, {
        icon: React.ReactNode;
        titleKey: string;
        messageKey: string;
        bgColor: string;
        borderColor: string;
    }> = {
        [JoinRequestStatus.ACCEPTED]: {
            icon: (
                <svg className="size-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            titleKey: 'employees.createForm.joinRequest.status.accepted.title',
            messageKey: 'employees.createForm.joinRequest.status.accepted.message',
            bgColor: 'bg-green-50 dark:bg-green-900/20',
            borderColor: 'border-green-200 dark:border-green-800',
        },
        [JoinRequestStatus.REJECTED]: {
            icon: (
                <svg className="size-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            titleKey: 'employees.createForm.joinRequest.status.rejected.title',
            messageKey: 'employees.createForm.joinRequest.status.rejected.message',
            bgColor: 'bg-red-50 dark:bg-red-900/20',
            borderColor: 'border-red-200 dark:border-red-800',
        },
        [JoinRequestStatus.CANCELLED]: {
            icon: (
                <svg className="size-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
            ),
            titleKey: 'employees.createForm.joinRequest.status.cancelled.title',
            messageKey: 'employees.createForm.joinRequest.status.cancelled.message',
            bgColor: 'bg-surface',
            borderColor: 'border-border',
        },
    };

    const config = statusConfig[joinRequestStatus as Exclude<JoinRequestStatus, JoinRequestStatus.PENDING>];

    if (config) {
        return (
            <div className={`flex flex-col items-center justify-center py-12 space-y-4 rounded-lg border ${config.bgColor} ${config.borderColor}`}>
                {config.icon}
                <h3 className="text-lg font-semibold text-onsurface">{t(config.titleKey)}</h3>
                <p className="text-sm text-muted-foreground text-center px-4">{t(config.messageKey)}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4 rounded-lg border bg-surface border-border">
            <svg className="size-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.052 12.952l1.414 1.414m0 0L12 14.414l5.534-5.534" />
            </svg>
            <h3 className="text-lg font-semibold text-onsurface">{t('employees.createForm.joinRequest.status.unknown.title')}</h3>
            <p className="text-sm text-muted-foreground text-center px-4">{t('employees.createForm.joinRequest.status.unknown.message')}</p>
        </div>
    );
}